import {
  CheckmarkFilled,
  CircleDash,
  Incomplete,
  Pause,
} from '@carbon/react/icons';
import type { ColumnFiltersState } from '@tanstack/react-table';

import type { ConfirmTractEntry, ConfirmTractsRequest } from './atoms';
import type {
  Confirmation,
  ConfirmationSource,
  PerTractConfirmationDraft,
  SlotCertificationStatus,
  SlotInterestRow,
  SlotsParams,
  SlotSortAttribute,
  SlotSubtract,
  SlotView,
  StreamCode,
  TractDraft,
} from './types';
import { wellStatusLabel } from 'utils/well-status';

/** Cell-colouring threshold for v1 (constant; Group C wires dynamic). */
export const DEFAULT_THRESHOLD = 1;

export const DEFAULT_PARAMS: Omit<SlotsParams, 'workspaceId'> = {
  page: 1,
  pageSize: 100,
  sortAttribute: 'slot_id',
  sortDirection: 'ASC',
};

export const STREAM_LABELS: Record<StreamCode, string> = {
  oil: 'Oil',
  gas: 'Gas',
  ngl: 'NGL',
};

/**
 * Joins stream codes into a human label (e.g. `Oil, Gas`).
 *
 * @param {StreamCode[]} streams - The row's canonically-ordered stream codes.
 * @return {string} Comma-joined stream labels.
 */
export const streamsLabel = (streams: StreamCode[]): string =>
  streams.map((stream) => STREAM_LABELS[stream]).join(', ');

// Sidebar STATUS filter — the three promoted-grain states (matching the Status
// column vocabulary) plus two overlays (INS-9896): On Hold (any active hold,
// carried or session) and Unpromoted Activity (changed this session — the blue
// highlight set).
export const STATUS_OPTIONS: Array<{ value: SlotView; label: string }> = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'certified', label: 'Certified' },
  { value: 'on_hold', label: 'Needs Further Review' },
  { value: 'unpromoted_activity', label: 'Unpromoted Activity' },
];

export const STREAM_OPTIONS: Array<{ value: StreamCode; label: string }> = (
  Object.entries(STREAM_LABELS) as [StreamCode, string][]
).map(([value, label]) => ({ value, label }));

// Sidebar hole-direction filter. Only the two common directions for now;
// directional/unknown wells can be added when product asks for them.
export const HOLE_DIRECTION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'VERTICAL', label: 'Vertical' },
  { value: 'HORIZONTAL', label: 'Horizontal' },
];

// Display labels for the normalized well-status codes the BE emits on each
// slot row (`wellStatus`) and in the filter-values `wellStatuses` array. Used
// for BOTH the Well Status column cell and the Well Status filter options; any
// unmapped code falls back to the raw value at the call site.
export const WELL_STATUS_LABELS: Record<string, string> = {
  pdp: 'PDP',
  duc: 'DUC',
  permit: 'Permit',
  expired_permit: 'Expired Permit',
  shut_in: 'Shut In',
  inactive: 'Inactive',
  // The Create New Well statuses (INS-13300): IC slots carry them since INS-13299. One row per
  // slot, so each keeps its own label rather than the grouped Pre-Permit bucket.
  proposal: wellStatusLabel('proposal'),
  planned: wellStatusLabel('planned'),
  pre_permit: wellStatusLabel('pre_permit'),
  apd: wellStatusLabel('apd'),
};

// Whether the slot's unit has a live Allocation Manager or is GIS-only.
export const AM_PRESENCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'has_am', label: 'Allocation Manager' },
  { value: 'gis_only', label: 'GIS Estimate Only' },
];

/**
 * Interest Source (taxonomy) labels. `allocation_manager` and `user_entered`
 * intentionally both map to `Internal` per the source-labels table in
 * `requirements.md` — the Interest Source column groups by who owns the
 * value, while `EVIDENCE_SOURCE_LABELS` below names the document.
 */
export const INTEREST_SOURCE_LABELS: Record<ConfirmationSource, string> = {
  allocation_manager: 'Internal',
  division_order: 'Operator/Payment-Based',
  user_entered: 'Internal',
};

export const EVIDENCE_SOURCE_LABELS: Record<ConfirmationSource, string> = {
  allocation_manager: 'Allocation Manager',
  division_order: 'Division Order',
  user_entered: 'Custom Interest',
};

/**
 * True when the slot is currently confirmed to Allocation Manager. Centralizes
 * the `'allocation_manager'` literal so cell renderers, validators, and
 * derivation paths all express AM-confirmation the same way.
 *
 * @param {Confirmation | null} confirmation - The slot's active confirmation.
 * @return {boolean} Whether the active source is Allocation Manager.
 */
export const isAmConfirmed = (confirmation: Confirmation | null): boolean =>
  confirmation?.source === 'allocation_manager';

/**
 * Renders the Interest Source taxonomy label for a slot — `Internal`,
 * `Operator/Payment-Based`, etc. — or em-dash when the slot is unconfirmed.
 * Shared by the parent comparison-table cell and the nested subtracts-table
 * cell so both surfaces label the source the same way.
 *
 * @param {Confirmation | null} confirmation - The slot's active confirmation.
 * @return {string} The taxonomy label or em-dash.
 */
export const interestSourceLabel = (
  confirmation: Pick<Confirmation, 'source'> | null | undefined
): string => {
  return confirmation === null || confirmation === undefined
    ? '—'
    : INTEREST_SOURCE_LABELS[confirmation.source];
};

/**
 * Renders the Evidence Source label — the document or system that justified
 * the certified value — or em-dash when the slot is unconfirmed.
 *
 * @param {Confirmation | null} confirmation - The slot's active confirmation.
 * @return {string} The evidence label or em-dash.
 */
export const evidenceSourceLabel = (
  confirmation: Pick<Confirmation, 'source'> | null | undefined
): string => {
  return confirmation === null || confirmation === undefined
    ? '—'
    : EVIDENCE_SOURCE_LABELS[confirmation.source];
};

/**
 * Returns the per-subtract Certified NRI/WI pair for a given slot confirmation:
 * - AM-confirmed slot → the subtract's AM values (BE doesn't echo per-subtract
 *   Certified values for AM, so they're derived from the per-subtract AM).
 * - Otherwise (DO / Custom / unconfirmed) → the subtract's `confirmedNri` /
 *   `confirmedWi`, which the BE populates from the tract's active
 *   per-subtract confirmation. Null on both sides when unconfirmed.
 *
 * Centralizes the derivation that the nested subtracts table's row-inflation
 * builder and Certified Interest cell renderer both consume.
 *
 * @param {Confirmation | null} confirmation - The slot's active confirmation.
 * @param {SlotSubtract} subtract - The per-subtract values (only `amNri`, `amWi`, `confirmedNri`, `confirmedWi` are read).
 * @return {object} The displayed `{ nri, wi }` pair (each may be null).
 */
export const getCertifiedPair = (
  confirmation: Confirmation | null,
  subtract: Pick<
    SlotSubtract,
    'amNri' | 'amWi' | 'confirmedNri' | 'confirmedWi' | 'confirmation'
  >
): { nri: number | null; wi: number | null } => {
  // Per-tract confirmation (partial confirmations) wins when present: AM
  // tracts certify the live per-subtract AM values; DO/Custom carry their
  // own confirmed values.
  const tractConfirmation = subtract.confirmation;
  if (tractConfirmation) {
    return tractConfirmation.source === 'allocation_manager'
      ? { nri: subtract.amNri, wi: subtract.amWi }
      : {
          nri: tractConfirmation.confirmedNri,
          wi: tractConfirmation.confirmedWi,
        };
  }
  if (isAmConfirmed(confirmation)) {
    return { nri: subtract.amNri, wi: subtract.amWi };
  }
  return { nri: subtract.confirmedNri, wi: subtract.confirmedWi };
};

/**
 * The streams a Certify submit should actually send: the ones the user selected
 * that are still selectable (not already certified) AND not on hold. A
 * disabled-but-checked box (held or already-certified) never rides along — this
 * mirrors the checkbox `disabled` gate so the submit can't include a locked
 * stream.
 *
 * @param {StreamCode[]} selectedStreams - The draft's checked streams.
 * @param {StreamCode[]} selectableStreams - Streams the editor allows (this card's scope, uncertified).
 * @param {StreamCode[]} heldStreams - Streams currently on hold for the tract.
 * @return {StreamCode[]} The streams safe to submit.
 */
export const submittableStreams = (
  selectedStreams: StreamCode[],
  selectableStreams: StreamCode[],
  heldStreams: StreamCode[]
): StreamCode[] =>
  selectedStreams.filter(
    (stream) =>
      selectableStreams.includes(stream) && !heldStreams.includes(stream)
  );

/** One row of the subtracts subtable: a single certification act (a
 *  confirmation group), the held streams, or the uncertified remainder. */
export interface SubtractActRow extends SlotSubtract {
  /** Stable unique id for the table (a subtract spans multiple rows now). */
  rowId: string;
  actKind: 'certified' | 'on_hold' | 'not_started';
  /** Streams this row represents. */
  actStreams: StreamCode[];
  /** Evidence source for a certified act; null for hold / not-started rows. */
  actSource: ConfirmationSource | null;
  actNri: number | null;
  actWi: number | null;
  actConfirmedByName: string | null;
  actConfirmedAt: string | null;
  numberOfLines?: number;
}

/**
 * Explodes a slot's subtracts into one subtable row per certification act, so a
 * subtract certified across multiple sources/values (e.g. oil/gas via AM, ngl
 * via Custom) shows a separate row per act instead of collapsing to one
 * source + one NRI/WI. Mirrors the Confirm panel's per-act cards: one row per
 * confirmation group, a row for any held streams, and — for an entirely
 * uncertified, unheld subtract — a single Not Started row so it stays visible.
 *
 * @param {SlotInterestRow} slot - The parent slot (subtracts + streams).
 * @return {SubtractActRow[]} The flattened per-act rows, in subtract order.
 */
export const subtractActRows = (slot: SlotInterestRow): SubtractActRow[] =>
  slot.subtracts.flatMap((subtract) => {
    const base = (
      rowId: string,
      extra: Partial<SubtractActRow>
    ): SubtractActRow => ({
      ...subtract,
      rowId,
      actKind: 'not_started',
      actStreams: [],
      actSource: null,
      actNri: null,
      actWi: null,
      actConfirmedByName: null,
      actConfirmedAt: null,
      ...extra,
    });

    const groups = subtract.confirmationGroups ?? [];
    const held = subtract.hold?.heldStreams ?? [];
    // Per-UAT grain (ONCALL-668): the read model fans a subtract out one entry
    // per UAT, so the rowId prefix includes the UAT to keep two same-subtract /
    // different-UAT segments distinct in the table.
    const seg = `${subtract.subtractId}-${subtract.uatId ?? NULL_UAT_KEY}`;
    const rows: SubtractActRow[] = groups.map((group) => {
      const isAm = group.source === 'allocation_manager';
      // source + streams: a subtract can have two same-source acts at different
      // values, so source alone would collide.
      return base(`${seg}-grp-${group.source}-${group.streams.join('-')}`, {
        actKind: 'certified',
        actStreams: group.streams,
        actSource: group.source,
        // AM has no entered value — show the live per-subtract figure; DO/Custom
        // carry their own confirmed values.
        actNri: isAm ? subtract.amNri : group.confirmedNri,
        actWi: isAm ? subtract.amWi : group.confirmedWi,
        actConfirmedByName: group.confirmedByName,
        actConfirmedAt: group.confirmedAt,
        numberOfLines: 2,
      });
    });

    if (subtract.onHold && held.length > 0) {
      rows.push(
        base(`${seg}-hold`, {
          actKind: 'on_hold',
          actStreams: held,
        })
      );
    }

    // Keep an entirely uncertified, unheld subtract visible with one row.
    if (rows.length === 0) {
      rows.push(base(`${seg}-none`, { actStreams: slot.streams }));
    }

    return rows;
  });

/**
 * Resolves a row's PROMOTED-grain certification status. Falls back to the
 * row's own status field when the BE hasn't sent `tractProgress` (the
 * single-slot confirm response skips the consolidation fold).
 *
 * @param {SlotInterestRow} row - The slot row.
 * @return {SlotCertificationStatus} not_started | in_progress | certified.
 */
export const slotStatus = (row: SlotInterestRow): SlotCertificationStatus =>
  row.tractProgress?.status ?? row.status ?? 'not_started';

/** Display labels for the promoted-grain certification status. */
export const SLOT_STATUS_LABELS: Record<SlotCertificationStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  certified: 'Certified',
};

/** Certification status incl. the On Hold overlay (which isn't a promoted-grain
 *  `SlotCertificationStatus`). */
export type CertificationDisplayStatus = SlotCertificationStatus | 'on_hold';

/**
 * Single source of truth for the status pill's icon + label, shared by the
 * comparison table, the subtracts subtable, and the Confirm-panel card header
 * (each keeps its own colour class, which lives in its own scss module).
 */
export const STATUS_DISPLAY: Record<
  CertificationDisplayStatus,
  { Icon: typeof CircleDash; label: string }
> = {
  not_started: { Icon: CircleDash, label: 'Not Started' },
  in_progress: { Icon: Incomplete, label: 'In Progress' },
  certified: { Icon: CheckmarkFilled, label: 'Certified' },
  on_hold: { Icon: Pause, label: 'Needs Further Review' },
};

/**
 * The certified-progress fraction shown beside the status, mirroring the main
 * comparison table's "Subtracts" column. Prefers live (session) progress and
 * falls back to the promoted-grain count; null when neither is present (e.g.
 * the single-slot confirm response). Lets a partially-certified-but-held slot
 * still show how far it got (e.g. "2/4 subtracts" next to In Progress · On Hold).
 *
 * Also appends the per-UAT ("interest") fraction (ONCALL-668), always showing
 * both grains, e.g. "0/1 subtracts 1/2 UATs".
 *
 * @param {SlotInterestRow} row - The slot row.
 * @return {string | null} e.g. "2/4 subtracts 3/8 UATs", or null when no
 *   progress data.
 */
export const subtractsFraction = (row: SlotInterestRow): string | null => {
  const progress = row.liveTractProgress ?? row.tractProgress;
  if (!progress) return null;
  return (
    `${progress.fullyCertifiedTracts}/${progress.totalTracts} subtracts ` +
    `${progress.certifiedUats}/${progress.totalUats} UATs`
  );
};

// `status`, `reconciledTo`, `evidenceSource`, and `electedInterest` all map to
// `source` intentionally — the BE exposes one logical sort over
// `confirmation.source` (NULLS LAST) that drives the confirmed-first order
// across all four visible columns.
const SORT_KEY_TO_ATTRIBUTE: Record<string, SlotSortAttribute> = {
  slotId: 'slot_id',
  unitName: 'unit_name',
  unitAgreementType: 'unit_agreement_type',
  operator: 'operator',
  streams: 'stream',
  status: 'source',
  reconciledTo: 'source',
  evidenceSource: 'source',
  electedInterest: 'source',
  amNri: 'am_nri',
  amWi: 'am_wi',
  doNri: 'do_nri',
  doWi: 'do_wi',
  nriDiff: 'nri_diff',
  wiDiff: 'wi_diff',
  confirmedAt: 'confirmed_at',
};

/**
 * Resolves a TanStack column id to the BE's `sort_attribute` enum, falling
 * back to `slot_id` (the BE default) for unknown ids.
 *
 * @param {string | undefined} columnId - TanStack column id.
 * @return {SlotSortAttribute} BE enum value.
 */
export const resolveSortAttribute = (
  columnId: string | undefined
): SlotSortAttribute =>
  (columnId && SORT_KEY_TO_ATTRIBUTE[columnId]) || 'slot_id';

/**
 * Translates TanStack column-filter state into the slot-list request payload.
 *
 * **Why pre-seed all three keys to `undefined`:** removing a filter at the UI
 * must clear it on the wire. If we omitted the key, the BE would keep the
 * previous server-side filter.
 *
 * @param {ColumnFiltersState} filters - TanStack column filter array.
 * @return {Partial<SlotsParams>} Partial params with the three filter fields.
 */
export const columnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<SlotsParams> => {
  const params: Partial<SlotsParams> = {
    view: undefined,
    streams: undefined,
    operators: undefined,
    holeDirections: undefined,
    wellStatuses: undefined,
    amPresence: undefined,
  };

  for (const filter of filters) {
    if (!Array.isArray(filter.value)) continue;

    switch (filter.id) {
      case 'view':
        params.view = filter.value as SlotView[];
        break;
      case 'streams':
        params.streams = filter.value as StreamCode[];
        break;
      case 'operator':
        params.operators = filter.value as string[];
        break;
      case 'holeDirection':
        params.holeDirections = filter.value as string[];
        break;
      case 'wellStatus':
        params.wellStatuses = filter.value as string[];
        break;
      case 'amPresence':
        params.amPresence = filter.value as string[];
        break;
    }
  }

  return params;
};

// ───────────────────────────────────────────────────────────────────────────
// Confirmation workflow — per-tract draft constants, validators, payloads
// ───────────────────────────────────────────────────────────────────────────

/** Draft-map key for the no-subtract pseudo-tract (slot-level pathway). */
export const SLOT_LEVEL_TRACT_KEY = '__slot__';

/** Draft-map sub-token for a NULL UAT (slot-level / no-UAT segment). */
export const NULL_UAT_KEY = '__nouat__';

/**
 * Draft-map key for a tract SEGMENT — keyed on `(subtractId, uatId)` so each
 * UAT of a subtract gets its own card / draft / note (ONCALL-668 per-UAT
 * grain). The read model fans subtracts out one row per UAT, so two UAT
 * segments of the same subtract must be two independent draft keys.
 *
 * Falls back to the pseudo-tract key for the no-subtract (slot-level) pathway,
 * and to a stable NULL-UAT sub-token when the segment has no UAT — both keep
 * the same key shape so the slot-level / null-UAT cases behave as before.
 *
 * @param {string | null} subtractId - The tract's subtract id.
 * @param {string | null} [uatId] - The segment's UAT id (null = no UAT).
 * @return {string} The draft-map key.
 */
export const tractKey = (
  subtractId: string | null,
  uatId: string | null = null
): string => {
  const subtractToken = subtractId ?? SLOT_LEVEL_TRACT_KEY;
  return `${subtractToken}::${uatId ?? NULL_UAT_KEY}`;
};

/** A pristine per-tract form value: no tract drafts yet. */
export const EMPTY_PER_TRACT_DRAFT: PerTractConfirmationDraft = { tracts: {} };

/** Debounce before firing the live auto-confirm threshold POST. */
export const AUTO_CONFIRM_INPUT_DEBOUNCE_MS = 300;

/**
 * True when `raw` parses to a number within the [0, 100] auto-confirm
 * threshold range. Shared by the threshold input and the toolbar label so both
 * treat the same draft values as valid.
 *
 * @param {string} raw - The raw threshold input string.
 * @return {boolean} Whether the value is a valid threshold.
 */
export const isValidThreshold = (raw: string): boolean => {
  const num = Number(raw);
  return raw.trim() !== '' && !Number.isNaN(num) && num >= 0 && num <= 100;
};

/**
 * Stable per-row key — mirrors the DataTable `getRowId` (workspaceSlotId +
 * streams) so bulk selection can match rows across a refetch.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @return {string} The composite row key.
 */
export const rowKey = (row: SlotInterestRow): string =>
  `${row.workspaceSlotId}-${row.streams.join('-')}`;

/**
 * True when the AM evidence source can be selected for the row — at least one
 * of `amNri` / `amWi` is non-null. Shared by the radio-disable, single-slot
 * submit gate, and bulk eligibility so all three express the relaxed AM rule
 * the same way. The BE persists nulls through; full presence-validation
 * relaxation lands with the BE follow-up.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @return {boolean} Whether AM is a usable source for this row.
 */
export const isAmSourceSelectable = (row: SlotInterestRow): boolean =>
  row.amNri !== null || row.amWi !== null;

/**
 * A row can be bulk-confirmed to Allocation Manager when AM is selectable and
 * the row hasn't been deactivated.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @return {boolean} Whether the row is eligible for AM bulk confirm.
 */
export const isEligibleForBulkAm = (row: SlotInterestRow): boolean =>
  isAmSourceSelectable(row) && !row.deactivated;

/**
 * The single UAT a bulk AM confirm should target for a row, or null to confirm
 * the whole slot (ONCALL-668). The BE keys one `unit_allocation_tract_id` per
 * slot_id, so we only narrow when the row's subtracts resolve to exactly one
 * distinct non-null UAT; a multi-UAT row (or one with no UAT) stays whole-slot
 * — the pre-existing bulk pathway.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @return {string | null} The single UAT id, or null for the whole-slot path.
 */
export const bulkUatForRow = (row: SlotInterestRow): string | null => {
  const uatIds = new Set(
    (row.subtracts ?? [])
      .map((subtract) => subtract.uatId)
      .filter((uatId): uatId is string => uatId !== null)
  );
  return uatIds.size === 1 ? [...uatIds][0] : null;
};

/**
 * The streams already certified for a tract within this row: the tract's own
 * `certifiedStreams` (the BE mirrors a slot-level confirmation onto every
 * tract it covers), or — for the no-subtract pseudo-tract — the row's streams
 * when the slot-level confirmation exists.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @param {SlotSubtract | null} subtract - The tract, or null for the pseudo-tract.
 * @return {StreamCode[]} The certified streams.
 */
export const certifiedStreamsFor = (
  row: SlotInterestRow,
  subtract: SlotSubtract | null
): StreamCode[] => {
  if (subtract) return subtract.confirmation?.certifiedStreams ?? [];
  return row.confirmation ? row.streams : [];
};

/**
 * Every stream certified on a row, unioned across the slot-level confirmation
 * and all per-tract confirmations, in canonical row order. Drives the
 * "uncertify all" bulk-unconfirm call — passing the full certified set in one
 * request lets the BE stamp a shared `unconfirmed_at` so the activity feed
 * merges the cleared streams into a single card.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @return {StreamCode[]} The row's certified streams, in row order.
 */
export const certifiedStreamsForRow = (row: SlotInterestRow): StreamCode[] => {
  const certified = new Set<StreamCode>(certifiedStreamsFor(row, null));
  for (const subtract of row.subtracts ?? []) {
    for (const stream of certifiedStreamsFor(row, subtract)) {
      certified.add(stream);
    }
  }
  return row.streams.filter((stream) => certified.has(stream));
};

/**
 * The row's streams NOT yet certified for the tract — the default selection
 * for a fresh per-tract confirm.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @param {SlotSubtract | null} subtract - The tract, or null for the pseudo-tract.
 * @return {StreamCode[]} The uncertified streams.
 */
export const uncertifiedStreamsFor = (
  row: SlotInterestRow,
  subtract: SlotSubtract | null
): StreamCode[] => {
  const certified = certifiedStreamsFor(row, subtract);
  return row.streams.filter((stream) => !certified.includes(stream));
};

/**
 * True when every one of the row's streams is certified for the tract — the
 * section then renders the certified summary with Update Certification.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @param {SlotSubtract | null} subtract - The tract, or null for the pseudo-tract.
 * @return {boolean} Whether the tract is fully certified.
 */
export const isTractFullyCertified = (
  row: SlotInterestRow,
  subtract: SlotSubtract | null
): boolean => {
  const certified = certifiedStreamsFor(row, subtract);
  return (
    certified.length > 0 && uncertifiedStreamsFor(row, subtract).length === 0
  );
};

/**
 * A pristine tract draft with the given stream pre-selection.
 *
 * @param {StreamCode[]} selectedStreams - Streams to pre-check.
 * @return {TractDraft} The empty draft.
 */
export const emptyTractDraft = (selectedStreams: StreamCode[]): TractDraft => ({
  source: null,
  selectedStreams,
  nri: null,
  wi: null,
  note: '',
});

/**
 * Seeds the per-tract form value for a row: one draft per tract (or one
 * pseudo-tract draft for no-subtract slots), each pre-selecting the tract's
 * uncertified streams.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @return {PerTractConfirmationDraft} The seeded form value.
 */
export const initialTractDrafts = (
  row: SlotInterestRow
): PerTractConfirmationDraft => {
  const tracts: Record<string, TractDraft> = {};
  const targets: Array<SlotSubtract | null> =
    row.subtracts.length > 0 ? row.subtracts : [null];
  for (const subtract of targets) {
    tracts[tractKey(subtract?.subtractId ?? null, subtract?.uatId ?? null)] = {
      ...emptyTractDraft(uncertifiedStreamsFor(row, subtract)),
      // Default the evidence source to Allocation Manager when it's usable for
      // the tract, so Certify is enabled the moment the panel opens (AM is the
      // primary pathway). Tracts with no usable AM values keep `source: null`
      // — the user picks DO or Custom.
      source: isTractAmSelectable(row, subtract) ? 'allocation_manager' : null,
    };
  }
  return { tracts };
};

/**
 * Whether the AM evidence source is usable for a tract — its live per-subtract
 * AM values exist (the pseudo-tract falls back to the slot-level rule).
 *
 * @param {SlotInterestRow} row - The slot row.
 * @param {SlotSubtract | null} subtract - The tract, or null for the pseudo-tract.
 * @return {boolean} Whether AM is selectable.
 */
export const isTractAmSelectable = (
  row: SlotInterestRow,
  subtract: SlotSubtract | null
): boolean => {
  // Estimated-NRI slots (no Allocation Manager on the unit) can never certify
  // to AM — their per-subtract NriCalculation values exist but are estimates.
  if (!isAmSourceSelectable(row)) return false;
  if (subtract) return subtract.amNri !== null || subtract.amWi !== null;
  return true;
};

/** The tract's primary (non-Custom) evidence value, labelled by what it is:
 *  "AM" when Allocation Manager is selectable, else "GIS" (an estimate that
 *  can't be certified to AM). */
export interface TractPrimarySource {
  label: 'AM' | 'GIS';
  /** Whether AM is a valid evidence source — drives the AM radio + row label. */
  amSelectable: boolean;
  nri: number | null;
  wi: number | null;
}

/**
 * The tract's primary source row for the Confirm panel — value plus whether it
 * is a real AM value or a GIS estimate. The slot-level live value is `amNri`/
 * `amWi` when AM-backed (`gisNri`/`gisWi` then carry the GIS comparison), else
 * `gisNri`/`gisWi` with the AM pair null, so preferring AM reads the in-use
 * value either way; the label and `amSelectable` flip when there's no
 * Allocation Manager.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @param {SlotSubtract | null} subtract - The tract, or null for the pseudo-tract.
 * @return {TractPrimarySource} The labelled primary source value.
 */
export const tractPrimarySource = (
  row: SlotInterestRow,
  subtract: SlotSubtract | null
): TractPrimarySource => {
  const amSelectable = isTractAmSelectable(row, subtract);
  return {
    label: amSelectable ? 'AM' : 'GIS',
    amSelectable,
    nri: subtract ? subtract.amNri : (row.amNri ?? row.gisNri),
    wi: subtract ? subtract.amWi : (row.amWi ?? row.gisWi),
  };
};

/**
 * Which calculation a GIS / AM value's "Show Calculation" explains: the stored
 * NriCalculation's own ('stored'), or — for a GIS value beside an AM value —
 * the GIS row of the manager's allocation method ('gis').
 *
 * @param {boolean} amSlot - Whether the slot's unit has an Allocation Manager.
 * @param {'am' | 'gis'} source - Which column pair the value is in.
 * @return {'stored' | 'gis'} The calculation to show.
 */
export const interestFormula = (
  amSlot: boolean,
  source: 'am' | 'gis'
): 'stored' | 'gis' => (source === 'gis' && amSlot ? 'gis' : 'stored');

/**
 * What one AM / GIS value cell of the subtracts accordion shows, or null for
 * an em-dash. AM cells carry a value only on AM-backed slots; GIS cells always
 * do. Every value gets the same popovers, each explaining that value: see
 * `interestFormula`.
 *
 * @param {boolean} amSlot - Whether the slot's unit has an Allocation Manager
 *   (`isAmSourceSelectable(slot)`).
 * @param {SlotSubtract} subtract - The subtract entry.
 * @param {'am' | 'gis'} source - Which column pair the cell is in.
 * @param {'NRI' | 'WI'} calculationType - Which value of the pair.
 * @return {object | null} The value and which calculation explains it.
 */
export const subtractNriCell = (
  amSlot: boolean,
  subtract: SlotSubtract,
  source: 'am' | 'gis',
  calculationType: 'NRI' | 'WI'
): { value: number | null; formula: 'stored' | 'gis' } | null => {
  if (source === 'am' && !amSlot) return null;

  const pair =
    source === 'am'
      ? { NRI: subtract.amNri, WI: subtract.amWi }
      : { NRI: subtract.gisNri, WI: subtract.gisWi };
  return {
    value: pair[calculationType],
    formula: interestFormula(amSlot, source),
  };
};

/**
 * Whether the DO evidence source is usable for a tract — its per-subtract DO
 * values exist. The pseudo-tract has no per-tract DO pathway.
 *
 * @param {SlotSubtract | null} subtract - The tract, or null for the pseudo-tract.
 * @return {boolean} Whether DO is selectable.
 */
export const isTractDoSelectable = (subtract: SlotSubtract | null): boolean =>
  // `?? null` guards the not-yet-sourced wire shape: the BE doesn't emit
  // per-subtract DO values today, so they arrive as `undefined` — which must
  // read as "no DO", not as a value (`undefined !== null` is true).
  subtract !== null &&
  (subtract.doNri ?? null) !== null &&
  (subtract.doWi ?? null) !== null;

/**
 * Whether a tract's Confirm button may submit: a source is chosen, at least
 * one stream is selected, and the source-specific inputs are satisfied
 * (custom requires an NRI + a supporting note).
 *
 * @param {SlotInterestRow} row - The slot row.
 * @param {SlotSubtract | null} subtract - The tract, or null for the pseudo-tract.
 * @param {TractDraft} draft - The tract's current draft.
 * @return {boolean} Whether Confirm Tract should be enabled.
 */
export const canSubmitTract = (
  row: SlotInterestRow,
  subtract: SlotSubtract | null,
  draft: TractDraft
): boolean => {
  if (draft.source === null || draft.selectedStreams.length === 0) return false;
  switch (draft.source) {
    case 'allocation_manager':
      return isTractAmSelectable(row, subtract);
    case 'division_order':
      return isTractDoSelectable(subtract);
    case 'user_entered':
      // Custom needs an NRI value here; it ALSO requires a supporting note (BE
      // rule), enforced separately at the Certify button against the card's own
      // `draft.note` (kept out of this predicate so the note can be empty while
      // other inputs validate).
      return draft.nri !== null;
    default:
      return false;
  }
};

/**
 * True when a single tract being re-certified currently has an ACTIVE
 * confirmation whose source is Custom (`user_entered`). Reads the per-tract
 * confirmation when present (partial-confirmations grain), falling back to the
 * slot-level confirmation for the no-subtract pseudo-tract.
 *
 * Drives the "switching to Allocation Manager overwrites your Custom values"
 * warning — only `user_entered → allocation_manager` should trip it.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @param {SlotSubtract | null} subtract - The tract, or null for the pseudo-tract.
 * @return {boolean} Whether the tract's active confirmation is Custom.
 */
export const hasPriorCustomConfirmation = (
  row: SlotInterestRow,
  subtract: SlotSubtract | null
): boolean => {
  const source = subtract?.confirmation?.source ?? row.confirmation?.source;
  return source === 'user_entered';
};

/**
 * True when a row (bulk grain) has any ACTIVE Custom (`user_entered`)
 * confirmation — either at the slot level or on any of its tracts. Bulk always
 * certifies to Allocation Manager, so any such row would have its Custom values
 * overwritten.
 *
 * @param {SlotInterestRow} row - The slot row.
 * @return {boolean} Whether the row carries any Custom confirmation.
 */
export const rowHasPriorCustomConfirmation = (row: SlotInterestRow): boolean =>
  row.confirmation?.source === 'user_entered' ||
  row.subtracts.some(
    (subtract) => subtract.confirmation?.source === 'user_entered'
  );

/**
 * Counts how many selected rows carry an active Custom confirmation — drives
 * the affected-count in the bulk warning message.
 *
 * @param {SlotInterestRow[]} rows - The selected rows.
 * @return {number} The number of rows with a prior Custom confirmation.
 */
export const countRowsWithPriorCustomConfirmation = (
  rows: SlotInterestRow[]
): number => rows.filter(rowHasPriorCustomConfirmation).length;

/** Digits required after the decimal point in the Custom Interest inputs.
 *  Certified interests are stored and compared at 8dp, so the user must type
 *  the full precision — zeros count (0.10000000, not 0.1). */
export const CUSTOM_INTEREST_DECIMALS = 8;

/**
 * Whether a Custom Interest input's typed text carries the required decimal
 * precision. Validates the raw string rather than the parsed number because
 * parsing loses trailing zeros ("0.10000000" parses to 0.1).
 *
 * @param {string} text - The raw numeric string as typed.
 * @return {boolean} Whether the text has >= CUSTOM_INTEREST_DECIMALS digits
 *   after the decimal point.
 */
export const hasCustomInterestPrecision = (text: string): boolean => {
  const dot = text.indexOf('.');
  return dot !== -1 && text.length - dot - 1 >= CUSTOM_INTEREST_DECIMALS;
};

/**
 * Divisor for `DeltaIndicator` percentages: `max(|am|, |do|)`. Null when both
 * sides are null (DeltaIndicator then renders the em-dash placeholder).
 *
 * @param {number | null} am - AM-side value (NRI or WI).
 * @param {number | null} doSide - DO-side value (NRI or WI).
 * @return {number | null} The divisor, or null when both sides are null.
 */
export const computeBase = (
  am: number | null,
  doSide: number | null
): number | null => {
  if (am === null && doSide === null) return null;
  return Math.max(Math.abs(am ?? 0), Math.abs(doSide ?? 0));
};

/**
 * Extracts a user-facing message from a rejected mutation, falling back when
 * the error is not an `Error` or carries no message text. Shared by the
 * Confirm Interest panel and the Promote modal so both surfaces handle BE
 * error envelopes identically.
 *
 * @param {unknown} error - The rejected value from a mutation atom's setter.
 * @param {string} fallback - Message when the error carries no usable text.
 * @return {string} The error message to surface inline.
 */
export const getMutationErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

/** The body of a failed write: an ApplicationError renders as `errors[]`. */
export type WriteFailure = { error?: string; errors?: { message?: string }[] };

/** The shape of a `request.post` result that `failedWriteMessage` reads. */
type WriteResult =
  | { type: 'error'; data?: WriteFailure }
  | { type: 'success' }
  | { type: 'fatal' | 'abort'; message: string };

/**
 * The message a failed write response carries, read the way `lib/request`
 * reads a failed GET: `error`, then the first `errors[]` message. A refusal's
 * message tells the user what to do (e.g. which wells to un-certify), so it
 * must reach the screen rather than a generic fallback.
 *
 * @param {WriteResult} response - The failed `request.post` result.
 * @param {string} fallback - Message when the response carries none.
 * @return {string} The message to throw.
 */
export const failedWriteMessage = (
  response: WriteResult,
  fallback: string
): string =>
  (response.type === 'error' &&
    (response.data?.error || response.data?.errors?.[0]?.message)) ||
  fallback;

/**
 * Builds one wire tract entry from a tract draft. AM omits values (the BE
 * certifies the live per-(subtract, slot) AM); DO submits the tract's own
 * doNri/doWi; Custom submits the entered values.
 *
 * @param {SlotSubtract | null} subtract - The tract, or null for the pseudo-tract.
 * @param {TractDraft} draft - The tract's draft (source must be chosen).
 * @return {ConfirmTractEntry} The wire entry.
 * @throws {Error} When `draft.source` is null — the section must disable
 *   Confirm until a source is chosen, so a null source here is a programming
 *   error.
 */
export const buildTractEntry = (
  subtract: SlotSubtract | null,
  draft: TractDraft
): ConfirmTractEntry => {
  const { source } = draft;
  if (source === null) {
    throw new Error('Cannot build a tract entry without a chosen source.');
  }

  const base = {
    subtract_id: subtract?.subtractId ?? null,
    // Per-UAT grain (ONCALL-668): thread the segment's UAT so the write path
    // targets the right `(workspace_slot, subtract, UAT)` row. Null for the
    // no-subtract / null-UAT (slot-level) pathway.
    unit_allocation_tract_id: subtract?.uatId ?? null,
    source,
    note: draft.note.trim() || null,
    streams: draft.selectedStreams,
  };
  if (source === 'allocation_manager') return base;
  if (source === 'division_order') {
    return {
      ...base,
      confirmed_nri: subtract?.doNri ?? 0,
      confirmed_wi: subtract?.doWi ?? 0,
    };
  }
  return {
    ...base,
    confirmed_nri: draft.nri ?? 0,
    confirmed_wi: draft.wi ?? 0,
  };
};

/**
 * Wraps tract entries into the atomic per-tract confirm body.
 *
 * @param {ConfirmTractEntry[]} entries - The tract entries to confirm.
 * @return {ConfirmTractsRequest} The request body.
 */
export const buildTractsPayload = (
  entries: ConfirmTractEntry[]
): ConfirmTractsRequest => ({ confirmation: { tracts: entries } });
